import { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { PhotoSubjectType, isBrandMarkSubject } from '@common/interfaces/photo'
import cn from 'classnames'
import Icon from '../Icon'
import ProfilePhoto, { type ProfilePhotoSize, loadPhoto, subscribeToPhoto } from '../ProfilePhoto'
import styles from './EditableProfilePhoto.module.scss'

// The cropper (react-easy-crop) loads on first open; this avatar is in the header on every page.
const ProfilePhotoEditor = lazy(() => import('../ProfilePhotoEditor'))

interface IProps {
  subjectType: PhotoSubjectType
  id: string
  name: string | null | undefined
  /** The photo as it stands — the detail payloads carry it, so the first paint needs no fetch. */
  photo?: string | null
  /** Non-person fallback (a Logo.dev mark) shown when there's no uploaded photo. */
  iconUrl?: string | null
  /** `contain` instead of `cover`, for company marks that shouldn't be cropped to the circle. */
  isLogo?: boolean
  size?: ProfilePhotoSize
  className?: string
  'data-cy'?: string
  /** Dialog title. Defaults to ProfilePhotoEditor's own. */
  editorTitle?: string
  /** A profile URL (LinkedIn) offered inside the editor for the copy-and-paste route in. */
  profileUrl?: string | null
  // Raw dispatched results — ProfilePhotoEditor inspects them for failure. See its IProps.
  onUpload: (file: File) => Promise<unknown>
  onRemove: () => Promise<unknown>
  /** Runs only after a confirmed success — typically a refetch. */
  onSaved?: () => Promise<void> | void
}

/**
 * A subject's photo with hover-to-edit, opening the shared ProfilePhotoEditor.
 *
 * The identity-block treatment used by the CRM person, company and organization pages. Extracted
 * once those three wanted the same thing rather than copied a third time — copying it is how the
 * codebase ended up with five profile-photo implementations in the first place.
 */
const EditableProfilePhoto = ({
  subjectType,
  id,
  name,
  photo,
  iconUrl,
  isLogo,
  size = 'large',
  className,
  'data-cy': dataCy,
  editorTitle,
  profileUrl,
  onUpload,
  onRemove,
  onSaved,
}: IProps) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  // Stays mounted after the first open so the dialog keeps its close transition.
  const [hasOpenedEditor, setHasOpenedEditor] = useState(false)

  // What the editor offers to Remove is the UPLOADED photo, and only that. Two things used to go
  // wrong when it was read off the `photo` prop with an `iconUrl` fallback: a caller that renders
  // the avatar straight from the cache (the global header) passes neither, so Remove never
  // appeared for a photo that plainly existed; and a caller that passes a Logo.dev mark as
  // `iconUrl` offered Remove for a fallback image with nothing stored behind it. Resolving it
  // here — from the same cache the avatar paints from — keeps the button honest for both.
  const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(photo ?? null)

  useEffect(() => {
    // A supplied `photo` seeds the value, but it does NOT stand in for the subscription: it is a
    // snapshot of the detail payload from when the page loaded, and the header uploads without
    // refetching that payload, so on its own it leaves Remove showing the pre-upload state until a
    // reload. Seed, subscribe regardless, and let the cache supersede it — the same precedence
    // ProfilePhoto applies to what it paints (`fetched !== undefined ? fetched : photo`).
    setUploadedPhoto(photo ?? null)

    if (!subjectType || !id) return undefined

    let isCurrent = true
    const read = () => {
      void loadPhoto(subjectType, id).then((resolved) => {
        if (isCurrent) setUploadedPhoto(resolved)
      })
    }

    // Only fetch when nothing was handed to us. Reading here unconditionally would put back the
    // exact request the detail payload's `photo` exists to avoid — loadPhoto goes to the network
    // on a cache miss. Once the editor saves, primePhoto fills the cache and notifies, so the
    // subscription's read is served from memory.
    if (photo === undefined) read()

    // The editor primes the cache on save, so this keeps Remove in step with what was just
    // uploaded or cleared without the dialog having to be reopened.
    const unsubscribe = subscribeToPhoto(subjectType, id, read)

    return () => {
      isCurrent = false
      unsubscribe()
    }
  }, [photo, subjectType, id])

  // Keep the button/overlay shape in step with the photo's — see isBrandMarkSubject.
  const isBrandMark = isLogo ?? isBrandMarkSubject(subjectType)

  const handleOpen = useCallback(() => {
    setHasOpenedEditor(true)
    setIsEditorOpen(true)
  }, [])
  const handleClose = useCallback(() => setIsEditorOpen(false), [])

  return (
    <>
      <button
        type="button"
        className={cn(styles.button, { [styles.brandMark]: isBrandMark }, className)}
        onClick={handleOpen}
        data-cy={dataCy}
        aria-label={`Change photo for ${name || 'this record'}`}
      >
        <ProfilePhoto
          name={name}
          subjectType={subjectType}
          id={id}
          photo={photo}
          iconUrl={iconUrl}
          isLogo={isLogo}
          size={size}
        />
        <span className={styles.overlay} aria-hidden="true">
          <Icon name="edit" size="md" />
        </span>
      </button>

      {hasOpenedEditor && (
        <Suspense fallback={null}>
          <ProfilePhotoEditor
            open={isEditorOpen}
            onCancel={handleClose}
            subjectType={subjectType}
            id={id}
            name={name || 'Record'}
            title={editorTitle}
            currentPhoto={uploadedPhoto}
            onUpload={onUpload}
            onRemove={onRemove}
            onSaved={onSaved}
            profileUrl={profileUrl}
          />
        </Suspense>
      )}
    </>
  )
}

export default EditableProfilePhoto
